import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { CurrencyInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getCurrencyColumns } from './CurrenciesAdminColumns';
import { CurrenciesAdminUpdateDialog } from './CurrenciesAdminUpdateDialog';

const CurrenciesAdminList: React.FC = () => {
    const [currencies, setCurrencies] = useState<CurrencyInterface[]>([]);
    const [currency, setCurrency] = useState<CurrencyInterface | null>(null);
    const [currencyId, setCurrencyId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currencyToDelete, setCurrencyToDelete] = useState<CurrencyInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [currencyToUpdate, setCurrencyToUpdate] = useState<CurrencyInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<CurrencyInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all currencies
    const handleGetAllCurrencies = async () => {
        setError(null);
        setCurrency(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<CurrencyInterface[]>>('/currencies');
            if (result.status && result.data) {
                setCurrencies(result.data);
            } else {
                setError(result.message || 'Failed to fetch currencies');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch currencies');
        }
    };

    // Fetch currency by id
    const handleGetCurrencyById = async () => {
        setError(null);
        setCurrencies([]);
        setSuccess(null);
        if (!currencyId) return setError('Please enter a Currency ID');
        try {
            const result = await apiGet<GenericResponseInterface<CurrencyInterface>>(`/currencies/${currencyId}`);
            if (result.status && result.data) {
                setCurrency(result.data);
            } else {
                setError(result.message || 'Failed to fetch currency');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch currency');
        }
    };

    // Delete currency
    const handleDeleteCurrency = async () => {
        if (!currencyToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/currencies/delete/${currencyToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Currency deleted successfully');
                setDeleteDialogOpen(false);
                setCurrencyToDelete(null);
                handleGetAllCurrencies();
            } else {
                setError(result.message || 'Failed to delete currency');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete currency');
        }
    };

    // Update currency
    const handleUpdateCurrency = async () => {
        if (!currencyToUpdate || !currencyToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<CurrencyInterface>>(
                `/currencies/update/${currencyToUpdate.id}`,
                'PUT',
                {
                    ...currencyToUpdate,
                    ...updateForm,
                }
            );
            if (result.status) {
                setSuccess(result.message || 'Currency updated successfully');
                setUpdateDialogOpen(false);
                setCurrencyToUpdate(null);
                setUpdateForm({});
                handleGetAllCurrencies();
            } else {
                setUpdateError(result.message || 'Failed to update currency');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update currency');
        }
    };

    const columns = getCurrencyColumns(
        setCurrencyToDelete,
        setDeleteDialogOpen,
        setCurrencyToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllCurrencies();
    }, []);

    useEffect(() => {
        if (updateDialogOpen && currencyToUpdate) {
            setUpdateForm({
                ...currencyToUpdate
            });
        }
    }, [updateDialogOpen, currencyToUpdate]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllCurrencies}>Get All Currencies</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetCurrencyById}>Get Currency by ID</Button>
                <TextField
                    label="Currency ID"
                    value={currencyId}
                    onChange={e => setCurrencyId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {currencies.length > 0 && (
                    <DataGrid
                        rows={currencies.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {currency && (
                    <DataGrid
                        rows={[{ ...currency, id: currency.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this currency?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteCurrency}>Delete</Button>
                </DialogActions>
            </Dialog>
            
            <CurrenciesAdminUpdateDialog
                open={updateDialogOpen}
                currencyToUpdate={currencyToUpdate}
                setCurrencyToUpdate={setCurrencyToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateCurrency}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default CurrenciesAdminList;